// src/pages/UserListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Typography,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { getAllUsers } from '../services/userService';
import CreateUserDialog from '../components/CreateUserDialog';
import EditUserDialog from '../components/EditUserDialog';
import UserActionsMenu from '../components/UserActionsMenu';

// Import common components
import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';

const UserListPage = () => {
  const navigate = useNavigate();

  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ role: '', zone: '', search: '' });

  // Snackbar for success / error notifications across all actions
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    // FIX: Do NOT setLoading(true) here — doing so unmounts DataTable and
    // destroys its internal page state. Use a separate subtle loading indicator
    // instead. We only set loading=true on the very first load (initial mount).
    setError('');
    try {
      const response = await getAllUsers(filters);
      setUsers(response.data.users);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    }
  }, [filters]);

  // Separate initial load flag so DataTable only shows skeleton on first render
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const load = async () => {
      setError('');
      try {
        const response = await getAllUsers(filters);
        setUsers(response.data.users);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load users');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    if (initialLoad) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleClearFilters = () => {
    setFilters({ role: '', zone: '', search: '' });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    // FIX: Do NOT clear menuUser here. UserActionsMenu captures the user
    // internally when the menu opens, so it is safe to clear menuUser only
    // after dialogs are fully closed (handled inside UserActionsMenu itself).
    // Clearing it here caused user to become null before dialog actions fired.
  };

  const handleMenuClosed = () => {
    // Called by UserActionsMenu after all dialogs are done — safe to clear now
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const getRoleColor = (role) => {
    if (role === 'ADMIN') return 'error';
    if (role === 'VERIFICATION_OFFICER') return 'primary';
    if (role === 'MANAGEMENT') return 'success';
    return 'default';
  };

  // Define table columns
  const columns = [
    {
      id: 'name',
      label: 'Name',
      render: (row) => {
        if (!row) return '-';
        return `${row.firstName || ''} ${row.lastName || ''}`.trim() || '-';
      },
    },
    {
      id: 'serviceNumber',
      label: 'Service Number',
      render: (row) => {
        if (!row?.serviceNumber) {
          return <Chip label="Not set" size="small" color="warning" variant="outlined" />;
        }
        return (
          <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
            {row.serviceNumber}
          </Typography>
        );
      },
    },
    {
      id: 'email',
      label: 'Email',
      render: (row) => row?.email || '-',
    },
    {
      id: 'role',
      label: 'Role',
      render: (row) => {
        if (!row?.role) return '-';
        return (
          <Chip
            label={row.role.replace(/_/g, ' ')}
            color={getRoleColor(row.role)}
            size="small"
          />
        );
      },
    },
    {
      id: 'assignedZone',
      label: 'Zone',
      render: (row) => row?.assignedZone || '—',
    },
    {
      id: 'isActive',
      label: 'Status',
      render: (row) => (
        <Chip
          label={row?.isActive ? 'Active' : 'Inactive'}
          color={row?.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'lastLogin',
      label: 'Last Login',
      render: (row) => {
        if (!row?.lastLogin) return 'Never';
        return new Date(row.lastLogin).toLocaleString();
      },
    },
  ];

  // Define row actions
  const rowActions = (row) => [
    {
      icon: <MoreIcon fontSize="small" />,
      label: 'More Actions',
      onClick: (event) => {
        setAnchorEl(event.currentTarget);
        setMenuUser(row);
      },
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <PageHeader
          title="User Management"
          onBack={() => navigate('/')}
          actions={[
            {
              label: 'Refresh',
              icon: <RefreshIcon />,
              onClick: fetchUsers,
              variant: 'outlined',
            },
            {
              label: 'Create User',
              icon: <AddIcon />,
              onClick: () => setCreateDialogOpen(true),
              variant: 'contained',
            },
          ]}
        />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Name, email or service number"
              sx={{ minWidth: 280 }}
              size="small"
            />
            <TextField
              select
              label="Role"
              name="role"
              value={filters.role}
              onChange={handleFilterChange}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="VERIFICATION_OFFICER">Verification Officer</MenuItem>
              <MenuItem value="MANAGEMENT">Management</MenuItem>
            </TextField>
            <TextField
              select
              label="Zone"
              name="zone"
              value={filters.zone}
              onChange={handleFilterChange}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Zones</MenuItem>
              <MenuItem value="ZONE A - LAGOS">Zone A - Lagos</MenuItem>
              <MenuItem value="ZONE B - KADUNA">Zone B - Kaduna</MenuItem>
              <MenuItem value="ZONE B2 - KADUNA">Zone B2 - Kaduna</MenuItem>
              <MenuItem value="ZONE C - BAUCHI">Zone C - Bauchi</MenuItem>
              <MenuItem value="ZONE D - MINNA">Zone D - Minna</MenuItem>
              <MenuItem value="ZONE E - OWERRI">Zone E - Owerri</MenuItem>
              <MenuItem value="ZONE F - IBADAN">Zone F - Ibadan</MenuItem>
              <MenuItem value="ZONE G - BENIN">Zone G - Benin</MenuItem>
              <MenuItem value="ZONE H - BENUE">Zone H - Benue</MenuItem>
              <MenuItem value="SERVICE HEADQUARTERS">Service Headquarters</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={fetchUsers} size="small">
              Apply Filters
            </Button>
            <Button variant="outlined" onClick={handleClearFilters} size="small">
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/*
          FIX: DataTable is ALWAYS mounted — never conditionally rendered.
          We pass loading={loading} so it shows the skeleton only on first load
          without unmounting itself (which would reset the page state).
          After initial load, loading stays false and refreshes are seamless.
        */}
        <DataTable
          columns={columns}
          data={users}
          loading={loading}
          rowActions={rowActions}
          emptyType="users"
          emptyMessage="No users found. Create a new user or adjust your filters."
        />

        {/* Total count */}
        {!loading && users.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total users: <strong>{users.length}</strong>
              {users.filter((u) => !u.serviceNumber).length > 0 && (
                <>
                  {' '}
                  •{' '}
                  <span style={{ color: '#ed6c02' }}>
                    {users.filter((u) => !u.serviceNumber).length} missing service number
                  </span>
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create User Dialog */}
      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onNotify={notify}
        onSuccess={() => {
          setCreateDialogOpen(false);
          fetchUsers();
        }}
      />

      {/* Edit User Dialog */}
      {selectedUser && (
        <EditUserDialog
          open={editDialogOpen}
          user={selectedUser}
          onNotify={notify}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setEditDialogOpen(false);
            setSelectedUser(null);
            fetchUsers();
          }}
        />
      )}

      {/* User Actions Menu */}
      <UserActionsMenu
        anchorEl={anchorEl}
        user={menuUser}
        onClose={handleCloseMenu}
        onFullClose={handleMenuClosed}
        onEdit={handleEdit}
        onSuccess={fetchUsers}
        onNotify={notify}
      />

      {/* Global success / error notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserListPage;